import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, CircularProgress } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isValidEmail } from '~/modules/helpers';

import { resetUserActions, updateUserProfile } from '~/actions';
import { STATUS } from '~/literals';

import { UserProfileFormValidationMessage, UserProfileParams, User, RootState } from '~/types';

function UserProfile() {
  const dispatch = useDispatch();
  const status = useSelector<RootState, string>(({ user }) => user.status || STATUS.IDLE);
  const user = useSelector<RootState, User | null>(({ user }) => user.user);
  const errorMessage = useSelector<RootState, string>(({ user }) => user?.errorMessage || '');

  useEffect(() => {
    dispatch(resetUserActions())
  }
    , []);

  const [validationMode, setValidationMode] = useState(false);
  const [userProfileParams, setRegisterParams] = useState<UserProfileParams>({
    email: user?.email || '',
    password: '',
    subscribe: false,
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });

  const [validationMessages, setValidationMessages] = useState<UserProfileFormValidationMessage>({
    email: [],
    password: [],
    firstName: [],
    lastName: [],

  });

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...userProfileParams,
      firstName: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getSearchValidationMessages(newRegisterParams));
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...userProfileParams,
      lastName: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getSearchValidationMessages(newRegisterParams));
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...userProfileParams,
      email: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getSearchValidationMessages(newRegisterParams));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...userProfileParams,
      password: event.target.value,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getSearchValidationMessages(newRegisterParams));
  };

  const handleSubscribeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegisterParams = {
      ...userProfileParams,
      subscribe: event.target.checked,
    } as UserProfileParams;

    setRegisterParams(newRegisterParams);
    setValidationMessages(getSearchValidationMessages(newRegisterParams));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const messages = getSearchValidationMessages(userProfileParams);

    setValidationMessages(messages);

    if (messages.email.length || messages.password.length) {
      setValidationMode(true);
    } else {
      dispatch(updateUserProfile(userProfileParams));
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      {
        user &&
        <Box
          sx={{
            marginTop: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <AccountCircleIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Update Account Information
          </Typography>
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {errorMessage ? <Alert sx={{ marginBottom: 2 }} severity="error">{errorMessage}</Alert> : ''}
            {status === STATUS.SUCCESS ? <Alert sx={{ marginBottom: 2 }} severity="success">User updated successfully</Alert> : ''}
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <TextField
                  autoComplete="given-name"
                  autoFocus
                  error={validationMode && validationMessages.firstName.length > 0}
                  fullWidth
                  helperText={
                    validationMode && validationMessages.firstName.length > 0
                      ? validationMessages.firstName.join('\n')
                      : null
                  }
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  onChange={handleFirstNameChange}
                  value={userProfileParams.firstName}
                  required
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  autoComplete="family-name"
                  error={validationMode && validationMessages.lastName.length > 0}
                  fullWidth
                  helperText={
                    validationMode && validationMessages.lastName.length > 0
                      ? validationMessages.lastName.join('\n')
                      : null
                  }
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  onChange={handleLastNameChange}
                  value={userProfileParams.lastName}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoComplete="email"
                  error={validationMode && validationMessages.email.length > 0}
                  fullWidth
                  helperText={
                    validationMode && validationMessages.email.length > 0
                      ? validationMessages.email.join('\n')
                      : null
                  }
                  id="email"
                  label="Email Address"
                  margin="normal"
                  name="email"
                  onChange={handleEmailChange}
                  placeholder="Email Address"
                  value={userProfileParams.email}
                  required
                  type="email"
                />
              </Grid>
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="password-content"
                    id="password-header"
                  >
                    <Typography>I want to change my password</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      error={validationMode && validationMessages.password.length > 0}
                      fullWidth
                      helperText={
                        validationMode && validationMessages.password.length > 0
                          ? validationMessages.password.join('\n')
                          : 'Leave blank if you do not wish to change the password.'
                      }
                      id="password"
                      label="Password"
                      margin="normal"
                      name="password"
                      onChange={handlePasswordChange}
                      value={userProfileParams.password}
                      placeholder="Password"
                      type="password"
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      onChange={handleSubscribeChange}
                      value="allowExtraEmails"
                    />
                  }
                  label="I want to receive inspiration, marketing promotions and updates via email."
                  value={userProfileParams.subscribe ? 'true' : 'false'}
                />
              </Grid>
            </Grid>
            <Button
              disabled={status === STATUS.RUNNING}
              fullWidth
              sx={{ mt: 3, mb: 2 }}
              type="submit"
              variant="contained"
            >
              {status === STATUS.RUNNING ? <CircularProgress color="primary" size={24} /> : 'Update Profile'}
            </Button>
          </Box>
        </Box>
      }
      {
        user === null &&
        <Box
          sx={{
            marginTop: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <AccountCircleIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            User not found
          </Typography>
        </Box>
      }
    </Container>
  );
}

export default UserProfile;

const getSearchValidationMessages = (fields: UserProfileParams): UserProfileFormValidationMessage => {
  const validationMessages: UserProfileFormValidationMessage = {
    password: [],
    email: [],
    firstName: [],
    lastName: [],
  };

  if (!fields.firstName) {
    validationMessages.firstName.push('* Please enter your first name.');
  }

  if (!fields.lastName) {
    validationMessages.lastName.push('* Please enter your last name.');
  }

  if (!fields.email || !isValidEmail(fields.email)) {
    validationMessages.email.push('* Please enter valid email address.');
  }

  return validationMessages;
};
